import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import api from "../../utils/api";
import { IMG_BASE_URL } from "../../constants";
import {
  Application,
  ApplicationsAPIResponse,
  FlattenedApplication,
  SortOptions,
} from "./applicationInterfaces";
import { generateImageUrl } from "../../utils/helpers";
import { AxiosResponse } from "axios";

const queryString = require("query-string");

export interface ApplicationsState {
  submissions: Application[];
  totalRecords: number;
  options: SortOptions & { offset: number };
  currentSubmissions: FlattenedApplication[];
  currentTotalRecords: number;
}

const initialState: ApplicationsState = {
  submissions: [],
  totalRecords: 0,
  options: { offset: 0, sort: "created_at", direction: "desc" },
  currentSubmissions: [],
  currentTotalRecords: 0,
};

export const getApplications = createAsyncThunk<
  Pick<ApplicationsState, "options" | "submissions" | "totalRecords">,
  SortOptions
>("applications/getApplications", async (options, thunkApi) => {
  const state: any = (thunkApi as any).getState();
  const newOptions = { ...state.applications.options, ...options };
  const query = queryString.stringify({
    ...newOptions,
    limit: 10,
  });
  const result = await api.get<ApplicationsAPIResponse>(
    `/organizations/${state.organization.id}/submissions?${query}`
  );
  const response = result.data;
  const submissions: Application[] = response.records.map((sub) => {
    const application: Application = {
      ...sub,
      benevolenceAdmin: {
        id: sub.benevolenceAdmin.id,
        firstName: sub.benevolenceAdmin.firstName,
        lastName: sub.benevolenceAdmin.lastName,
      },
      organization: {
        ...sub.organization,
        id: sub.organization.id,
        name: sub.organization.name,
        imgUrl: generateImageUrl(sub.organization),
      },
      user: {
        firstName: sub.user.firstName,
        lastName: sub.user.lastName,
        imgUrl: `${IMG_BASE_URL}/${sub.user.imageVersion}/${sub.user.imageId}`,
        userId: sub.user.id,
      },
    };
    return application;
  });

  return {
    options: newOptions,
    submissions: submissions,
    totalRecords: response.totalRecords,
  };
});

export const getApplicationsCurrent = createAsyncThunk<
  { currentSubmissions: FlattenedApplication[]; currentTotalRecords: number },
  {
    limit?: number;
    assignedToMe?: boolean;
    decisionStatus?: string;
    includeArchived?: boolean;
    dashboardFilter?: string;
  }
>("applications/getApplicationsCurrent", async (options, thunkApi) => {
  const state: any = (thunkApi as any).getState();
  let result: AxiosResponse<{
    totalRecords: number;
    records: FlattenedApplication[];
  }>;
  if (options?.dashboardFilter) {
    const query = queryString.stringify({
      limit: options?.limit,
      assignedToMe: options?.assignedToMe,
      dashboardFilter: options.dashboardFilter,
    });
    result = await api.get<{
      totalRecords: number;
      records: FlattenedApplication[];
    }>(
      `/organizations/${state.organization.id}/submissions/dashboard/details?${query}`
    );
  } else {
    const query = queryString.stringify({
      limit: options?.limit,
      assignedToMe: options?.assignedToMe,
      decisionStatus: options?.decisionStatus,
      includeArchived: options?.includeArchived,
    });
    result = await api.get<{
      totalRecords: number;
      records: FlattenedApplication[];
    }>(`/organizations/${state.organization.id}/submissions/current?${query}`);
  }
  const response = result.data;

  return {
    currentSubmissions: response.records,
    currentTotalRecords: response.totalRecords,
  };
});

export const applicationsSlice = createSlice({
  name: "applications",
  initialState,
  reducers: {},
  extraReducers: {
    [getApplications.fulfilled.type]: (
      state,
      action: PayloadAction<
        Pick<ApplicationsState, "options" | "submissions" | "totalRecords">
      >
    ) => {
      state.options = action.payload.options;
      state.submissions = action.payload.submissions;
      state.totalRecords = action.payload.totalRecords;
    },
    [getApplicationsCurrent.fulfilled.type]: (
      state,
      action: PayloadAction<{
        currentSubmissions: FlattenedApplication[];
        currentTotalRecords: number;
      }>
    ) => {
      state.currentSubmissions = action.payload.currentSubmissions;
      state.currentTotalRecords = action.payload.currentTotalRecords;
    },
  },
});

export const selectApplications = (state: RootState) => state.applications;

export default applicationsSlice.reducer;
